// The pod's whole journey, drawn as the same graph the "waiting on" sheet uses.
//
// The strip on the pod card only answers "how far along is it". This answers
// the rest of it: what has been done, what is running now, what is still to
// come, and — because a pod's route branches — which of those are on the same
// thread of work and which are running alongside.
//
// Reads forwards, the way the pod actually travels: the first operation at the
// top, the end of the route at the bottom, this pod's own step ringed in place.
// Anything unfinished is tappable, so the graph doubles as a way to get to the
// station that has the work.

import type { JourneyStepVM, PodVM } from '@/api/types';
import { Sheet } from '@/components/ui/Sheet';
import { EmptyState } from '@/components/ui/misc';
import { GraphRows } from '@/components/pod/GraphRows';
import { buildRouteGraph } from '@/lib/upstreamGraph';
import { useT } from '@/i18n/useT';

export function RouteSheet({
  open,
  pod,
  journey,
  onClose,
  onJumpTo,
}: {
  open: boolean;
  pod: PodVM;
  journey: JourneyStepVM[];
  onClose: () => void;
  /** Go to that work centre with this pod selected. */
  onJumpTo?: (step: JourneyStepVM) => void;
}) {
  const t = useT();
  const graph = buildRouteGraph(journey);
  const done = journey.filter((s) => s.state === 'done' || s.state === 'cancel').length;

  return (
    <Sheet open={open} onClose={onClose} variant="tall" title={t.podJourney}>
      <p style={{ font: '500 12px Manrope,sans-serif', color: 'var(--sub)', margin: '0 0 14px' }}>
        {[pod.mo, pod.proj, pod.type].filter(Boolean).join(' · ')}
      </p>

      {graph.nodes.length === 0 ? (
        <EmptyState>{t.waitingOnUnknown}</EmptyState>
      ) : (
        <>
          <p style={{ font: '500 12.5px Manrope,sans-serif', color: 'var(--ink)', margin: '0 0 4px' }}>
            {done} {t.routeOfDone} {journey.length} {t.routeOperations}
          </p>
          <p style={{ font: '500 11px Manrope,sans-serif', color: 'var(--sub)', margin: '0 0 12px' }}>
            {t.routeTapHint}
          </p>

          <GraphRows graph={graph} hereWoId={pod.woId} {...(onJumpTo ? { onJumpTo } : {})} />
        </>
      )}
    </Sheet>
  );
}
